import React, { useMemo } from 'react';

import energyIcon from 'assets/pics/energy_icon.svg';
import gasIcon from 'assets/pics/gas_icon.svg';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { CustomModal } from 'components/CustomModal';
import {
  calcHasEnergy,
  calcHasGas,
} from 'components/DebFormComponents/Table/useInstallerBranchStatus';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormField } from 'components/FormHelper/FormField';
import { SectionSubtitle } from 'components/FormHelper/SectionSubtitle';
import { LabelCheckbox } from 'components/LabelCheckbox/LabelCheckbox';

import {
  BRANCH_TYPE,
  EIV_STATUS_ALLOW_EXPERT_EDITING,
  VALIDATION_CONSTANTS,
} from 'global/constants';

import { canExtendBranch, isRegisteredBranchMain } from 'utils/InstallerUtils';
import { YFutureDate } from 'utils/YupUtils';

import './ExtendExpertPopup.sass';
import './Popup.sass';

export const ExtendExpertPopup = ({
  mainFormik,
  oldData,
  selectedRow,
  selectedBranch,
  popupOpen,
  setPopupOpen,
  setSelectedRow,
  installer,
  installerStatusSummary,
}) => {
  const expert = mainFormik.values.experts?.[selectedRow] ?? null;
  const oldExpert = useMemo(
    () => oldData.find((oldExpertData) => oldExpertData.id === expert?.id) ?? null,
    [oldData, expert?.id]
  );
  const isExpertEnergyAllowedToEdit = EIV_STATUS_ALLOW_EXPERT_EDITING.includes(
    expert?.statusEnergy
  );
  const isExpertGasAllowedToEdit = EIV_STATUS_ALLOW_EXPERT_EDITING.includes(expert?.statusGas);
  const isEnergyExtensionUpdated = Boolean(
    expert && oldExpert && expert.expertEnergyValidityDate !== oldExpert.expertEnergyValidityDate
  );
  const isGasExtensionUpdated = Boolean(
    expert && oldExpert && expert.expertGasValidityDate !== oldExpert.expertGasValidityDate
  );
  const canExtendEnergy =
    isExpertEnergyAllowedToEdit &&
    !isEnergyExtensionUpdated &&
    canExtendBranch(BRANCH_TYPE.ENERGY, expert?.expertEnergyValidityDate) &&
    !isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.ENERGY);
  const canExtendGas =
    isExpertGasAllowedToEdit &&
    !isGasExtensionUpdated &&
    canExtendBranch(BRANCH_TYPE.GAS, expert?.expertGasValidityDate) &&
    !isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.GAS);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        extendEnergy: Yup.boolean(),
        extendGas: Yup.boolean(),
        expertEnergyValidityDate: Yup.string().when('extendEnergy', {
          is: (extendEnergy) => canExtendEnergy && Boolean(extendEnergy),
          then: () => YFutureDate.required(VALIDATION_CONSTANTS.MANDATORY),
          otherwise: () => Yup.string(),
        }),
        expertGasValidityDate: Yup.string().when('extendGas', {
          is: (extendGas) => canExtendGas && Boolean(extendGas),
          then: () => YFutureDate.required(VALIDATION_CONSTANTS.MANDATORY),
          otherwise: () => Yup.string(),
        }),
      }),
    [canExtendEnergy, canExtendGas]
  );

  const initialValues = useMemo(
    () => ({
      expertEnergyValidityDate: expert?.expertEnergyValidityDate || '',
      expertGasValidityDate: expert?.expertGasValidityDate || '',
      extendEnergy:
        selectedBranch === BRANCH_TYPE.ENERGY ? canExtendEnergy : canExtendEnergy && !canExtendGas,
      extendGas:
        selectedBranch === BRANCH_TYPE.GAS ? canExtendGas : canExtendGas && !canExtendEnergy,
    }),
    [
      expert?.expertEnergyValidityDate,
      expert?.expertGasValidityDate,
      selectedBranch,
      canExtendEnergy,
      canExtendGas,
    ]
  );

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues,
  });

  const onCancel = () => {
    formik.resetForm();
    setSelectedRow(null);
    setPopupOpen(false);
  };

  const onSubmit = () => {
    if (!formik.isValid || !expert) {
      return;
    }

    const shouldExtendEnergy = canExtendEnergy && Boolean(formik.values.extendEnergy);
    const shouldExtendGas = canExtendGas && Boolean(formik.values.extendGas);
    if (!shouldExtendEnergy && !shouldExtendGas) {
      return;
    }

    mainFormik.setFieldValue(`experts[${selectedRow}]`, {
      ...expert,
      expertEnergyValidityDate:
        shouldExtendEnergy &&
        formik.values.expertEnergyValidityDate !== expert.expertEnergyValidityDate
          ? formik.values.expertEnergyValidityDate
          : expert.expertEnergyValidityDate,
      expertGasValidityDate:
        shouldExtendGas && formik.values.expertGasValidityDate !== expert.expertGasValidityDate
          ? formik.values.expertGasValidityDate
          : expert.expertGasValidityDate,
      ...(shouldExtendEnergy && { isExpertExtendedEnergy: true }),
      ...(shouldExtendGas && { isExpertExtendedGas: true }),
    });
    formik.resetForm();
    setSelectedRow(null);
    setPopupOpen(false);
  };

  const modalContent = () => {
    if (!expert) {
      return <div>Kein Eintrag ausgewählt</div>;
    }

    const hasEnergy = calcHasEnergy(installer);
    const hasGas = calcHasGas(installer);
    const showEnergy = hasEnergy && canExtendEnergy;
    const showGas = hasGas && canExtendGas;

    return (
      <div className="popup-content extend-expert-popup">
        <div className="extend-expert-popup__expert-name">
          Verlängerung für die Fachkraft{' '}
          <span className="edit-popup-expert-name">{expert.completeName}</span> beantragen.
        </div>
        <div className="popup-content__popup-field extend-expert-popup__branches">
          <SectionSubtitle>Für die Sparte:</SectionSubtitle>
          <div className="flex-row">
            {showEnergy && (
              <LabelCheckbox
                label="Strom"
                id="extendEnergy"
                name="extendEnergy"
                icon={energyIcon}
                value={formik.values.extendEnergy}
                disabled={!canExtendEnergy}
                onChange={formik.handleChange}
              />
            )}
            {showGas && (
              <LabelCheckbox
                label="Gas"
                id="extendGas"
                name="extendGas"
                icon={gasIcon}
                value={formik.values.extendGas}
                disabled={!canExtendGas}
                onChange={formik.handleChange}
              />
            )}
          </div>
        </div>
        {showEnergy && formik.values.extendEnergy && (
          <div className="popup-content__popup-field extend-expert-popup__branch-section">
            {showGas && formik.values.extendGas && <SectionSubtitle>Für Strom</SectionSubtitle>}
            <FormField
              name="expertEnergyValidityDate"
              formik={formik}
              label="Ausweis gültig bis*"
              oldValue=""
              fullWidth
            />
          </div>
        )}
        {showGas && formik.values.extendGas && (
          <div className="popup-content__popup-field extend-expert-popup__branch-section">
            {showEnergy && formik.values.extendEnergy && <SectionSubtitle>Für Gas</SectionSubtitle>}
            <FormField
              name="expertGasValidityDate"
              formik={formik}
              label="Ausweis gültig bis*"
              oldValue=""
              fullWidth
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <CustomModal
      isOpen={popupOpen}
      title="Eintragung verlängern"
      content={modalContent()}
      closeCancelEventModal={onCancel}
      ctaButtons={
        <div className="extend-expert-popup__actions">
          <FormActionButtons
            hasErrors={
              Object.keys(formik.errors).length > 0 ||
              (!formik.values.extendEnergy && !formik.values.extendGas)
            }
            titleLeft="Abbrechen"
            titleRight="Verlängern"
            onClickLeft={onCancel}
            onClickRight={onSubmit}
            errorMessage={
              formik.errors.expertEnergyValidityDate ||
              formik.errors.expertGasValidityDate ||
              (!formik.values.extendEnergy && !formik.values.extendGas
                ? 'Bitte wählen Sie eine Sparte'
                : '')
            }
          />
        </div>
      }
    />
  );
};

ExtendExpertPopup.propTypes = {
  mainFormik: PropTypes.object.isRequired,
  oldData: PropTypes.array.isRequired,
  selectedRow: PropTypes.number,
  selectedBranch: PropTypes.oneOf([BRANCH_TYPE.ENERGY, BRANCH_TYPE.GAS]),
  popupOpen: PropTypes.bool,
  setPopupOpen: PropTypes.func,
  setSelectedRow: PropTypes.func,
  installer: PropTypes.object,
  installerStatusSummary: PropTypes.object,
};

ExtendExpertPopup.defaultProps = {
  popupOpen: false,
  selectedBranch: null,
  setPopupOpen: () => {},
  setSelectedRow: () => {},
  selectedRow: null,
  installer: null,
  installerStatusSummary: null,
};
